import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
// import { useNavigate } from 'react-router-dom'; // We'll use onNavigate for now
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../components/ui/card';
import { Loader2, AlertTriangle } from 'lucide-react';
import { useNavigate } from 'react-router-dom'; // Import useNavigate

// interface LoginPageProps {
//   onNavigate: (page: 'home' | 'admin' | 'products' | 'about' | 'contact' | 'login') => void;
// } // onNavigate prop is removed

const LoginPage: React.FC = () => { // Removed onNavigate from props
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false); // Local loading state for form submission
  const [error, setError] = useState<string | null>(null);
  const { login, isAuthenticated, isLoading: authIsLoading } = useAuth(); // isLoading from useAuth is for initial auth check
  const navigate = useNavigate();

  useEffect(() => {
    // Wait for initial auth check to complete before redirecting
    if (!authIsLoading && isAuthenticated) {
      console.log("LoginPage: Already authenticated, navigating to /admin");
      navigate('/admin', { replace: true });
    }
  }, [isAuthenticated, authIsLoading, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true); // Use local isLoading for form submission
    setError(null);
    try {
      await login(username, password);
      // Successful login will trigger the useEffect above due to isAuthenticated change,
      // or we can navigate directly here as well.
      console.log("LoginPage: Login successful, navigating to /admin");
      navigate('/admin', { replace: true });
    } catch (err: any) {
      setError(err.message || 'Failed to login. Please check your credentials.');
    } finally {
      setIsLoading(false); // Use local isLoading
    }
  };

  // If initial auth check is still loading, show a message or spinner
  if (authIsLoading) {
    return <div>Loading authentication...</div>;
  }

  return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh' }}>
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>Admin Login</CardTitle>
          <CardDescription>Please enter your credentials to access the admin panel.</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit}>
            {error && (
              <div style={{ color: 'red', marginBottom: '1rem', display: 'flex', alignItems: 'center' }}>
                <AlertTriangle style={{ marginRight: '0.5rem' }}/>
                {error}
              </div>
            )}
            <div style={{ marginBottom: '1rem' }}>
              <label htmlFor="username" style={{ display: 'block', marginBottom: '0.5rem' }}>Username</label>
              <Input
                id="username"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="admin"
                disabled={isLoading} // Local isLoading for form
                required
              />
            </div>
            <div style={{ marginBottom: '1rem' }}>
              <label htmlFor="password" style={{ display: 'block', marginBottom: '0.5rem' }}>Password</label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="password"
                disabled={isLoading} // Local isLoading for form
                required
              />
            </div>
            <Button type="submit" disabled={isLoading} style={{ width: '100%' }}>
              {isLoading ? ( // Local isLoading for form
                <>
                  <Loader2 style={{ marginRight: '0.5rem', animation: 'spin 1s linear infinite' }} />
                  Logging in...
                </>
              ) : (
                'Login'
              )}
            </Button>
          </form>
        </CardContent>
        <CardFooter>
          <p style={{ fontSize: '0.8rem', color: '#666' }}>Use username 'admin' and password 'password' to login.</p>
        </CardFooter>
      </Card>
    </div>
  );
};

export default LoginPage;
